import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  IconButton,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
  Popover,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ProImg1 from "../../../../assets/images/AgentStar.svg";
import DescriptionIcon from "@mui/icons-material/Description";
import LanguageIcon from "@mui/icons-material/Language";
import CachedIcon from "@mui/icons-material/Cached";
import {
  KnowledgeBase,
  KnowledgeBaseCardMetaData,
  KnowledgeBaseFileData,
  KnowledgeBaseFileInfo,
} from "../../../Types/KnowledgeBase";
import { SettingsOutlined, Source } from "@mui/icons-material";
import variables from "../../../../assets/styles/variables.module.scss";
import {
  StartLoading,
  StopLoading,
} from "../../../Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import BaseInstance from "../../../../Services/AxiosInstance/BaseInstance";
import { ShowError } from "../../../Store/MessageReducer/MsgActions";
import { DASHBOARD_KNOWLEDGE_BASE, KNOWLEDGE_BASE } from "../../../../Services/Endpoint";
import { formatDate } from "../../../utils/Common/Common";
import { getFileIcon } from "../../../utils/File/FileIcon";
import Confirm from "../../../Types/Confirm";
import ConfirmPopup from "../../ConfirmPopup";
import BaseMultipartInstance from "../../../../Services/AxiosInstance/BaseMultupartInstance";
import CustomFileIcon from "../../../../assets/images/CustomFileIcon.svg";
import CustomLinkIcon from "../../../../assets/images/linkIcon.svg";
import { initialData } from "./kwCardMetaDataInitial";
import { KnowledgeBaseTypeEnum, KnowledgeBaseFileStatusEnum } from "../../../Enum/KnowledgeBaseEnum";
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

interface MycomponentProps {
  knowledgeBase: KnowledgeBase;
  deleteFun: (knowledgeBase: KnowledgeBase) => void;
  editFun: (knowledgeBase: KnowledgeBase) => void;
  getKnowledgeBases: () => void;
}

const KnowledgeBaseCard: React.FC<MycomponentProps> = (props) => {
  const dispatch = useDispatch();
  const [knowledgeBasesSource, setKnowledgeBasesSource] = useState<KnowledgeBaseCardMetaData>(initialData);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });

  const [filesLoading, setFilesLoading] = useState(false);
  const [linksLoading, setLinksLoading] = useState(false);



  const uploadSingleFile = (file: File, knowledgeId: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", "");
    formData.append("process_immediately", "false");
    return BaseMultipartInstance.post(
      `${KNOWLEDGE_BASE}/${knowledgeId}/upload`,
      formData
    );
  };

  const filesUploadKnowledgeBase = async (
    fileArr: File[],
    knowledgeId?: string
  ) => {
    try {
      dispatch(StartLoading());
      if (fileArr && fileArr.length > 0 && knowledgeId) {
        const uploadPromises = fileArr.map((file) =>
          uploadSingleFile(file, knowledgeId)
        );
        // uploadPromises.push(await BaseInstance.post(`${KNOWLEDGE_BASE}/${knowledgeId}/process-all`).then((res) => res?.data))
        await Promise.all(uploadPromises);
        await BaseInstance.post(
          `${KNOWLEDGE_BASE}/${knowledgeId}/process-all`
        ).then((res) => res?.data);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      const fileArr = Array.from(files);
      filesUploadKnowledgeBase(fileArr, props.knowledgeBase.id);
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading());
      const data = await BaseInstance.delete(
        `${KNOWLEDGE_BASE}/${props.knowledgeBase.id}/files/${fileId}`
      ).then((res) => res?.data);
      props.getKnowledgeBases();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };
  const getFileStatus = async () => {
    try {
      setFilesLoading(true);
      const data = await BaseInstance.get(`${KNOWLEDGE_BASE}/${props.knowledgeBase.id}/files`
      ).then((res) => res?.data);
      setKnowledgeBaseFiles(data);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      setFilesLoading(false);
    }
  };

  const confirmDelete = (fileId: string) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Confirm Delete",
      info: "Are you sure want to delete your data? This action cannot be undone.",
      varient: "delete",
      functionName: deleteFile,
      functionParam: fileId,
    });
  };

  const [filesPopoverAnchor, setFilesPopoverAnchor] =
    useState<null | HTMLElement>(null);
  const [linksPopoverAnchor, setLinkPopoverAnchor] =
    useState<null | HTMLElement>(null);

  useEffect(() => {
    if (filesPopoverAnchor) {
      getFileStatus();
    }
  }, [filesPopoverAnchor])

  const isCombinedKB = props.knowledgeBase.type === KnowledgeBaseTypeEnum.COMBINED;
  const handleFilesClick = (event: React.MouseEvent<HTMLElement>, type: string) => {
    if (type === "file") {
      if (
        isCombinedKB
          ? props.knowledgeBase.files && props.knowledgeBase.files.items && props.knowledgeBase.files.items.length > 0
          : props.knowledgeBase.files && props.knowledgeBase.files.count > 0
      ) {
        setFilesPopoverAnchor(event.currentTarget);
      }
    }
    if (type === "link") {
      if (
        isCombinedKB
          ? props.knowledgeBase.links && props.knowledgeBase.links.items && props.knowledgeBase.links.items.length > 0
          : props.knowledgeBase.links && props.knowledgeBase.links.count > 0
      ) {
        setLinkPopoverAnchor(event.currentTarget);
      }
    }
  };


  const handleFilesPopoverClose = () => setFilesPopoverAnchor(null);
  const handleLinkPopoverClose = () => setLinkPopoverAnchor(null);
  const filesPopoverOpen = Boolean(filesPopoverAnchor);
  const linkPopoverOpen = Boolean(linksPopoverAnchor);

  return (
    <Card
      sx={{
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(256, 256, 256, 0.7)",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          {/* <Avatar
            src={ProImg1}
            sx={{
              backgroundColor: "#04DB93",
              "& img": {
                objectFit: "none",
              },
            }}
          /> */}
          {props.knowledgeBase.type === KnowledgeBaseTypeEnum.PDF ? (
            <Box
              sx={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#05DABB",
                borderRadius: "50%",
              }}
            >
              <DescriptionIcon sx={{ color: "#fff" }} />
            </Box>
          ) : (
            <Box
              sx={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#05DABB",
                borderRadius: "50%",
              }}
            >
              <LanguageIcon sx={{ color: "#fff" }} />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexGrow: 1,
            }}
          >
            {props.knowledgeBase.name.length > 38 ? (
              <Tooltip title={props.knowledgeBase.name} placement="top-start">
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  noWrap
                  sx={{
                    maxWidth: 250,
                    lineHeight: "20px",
                    textTransform: "capitalize",
                  }}
                >
                  {props.knowledgeBase.name}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="subtitle1"
                fontWeight={500}
                noWrap
                sx={{
                  maxWidth: 250,
                  lineHeight: "20px",
                  textTransform: "capitalize",
                }}
              >
                {props.knowledgeBase.name}
              </Typography>
            )}

            <Typography
              variant="caption"
              noWrap
              sx={{
                maxWidth: 250,
                color: "#6E6E6E",
                textTransform: "capitalize",
              }}
            >
              {props.knowledgeBase.updated_at
                ? props.knowledgeBase.updated_at
                  ? formatDate(props.knowledgeBase.updated_at)
                  : "--"
                : props.knowledgeBase.created_at
                  ? formatDate(props.knowledgeBase.created_at)
                  : "--"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Sources
          </Typography>
          <Box sx={{ mt: 1 }}>
            {/* Files row */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={0.5}
            >
              <Box display="flex" alignItems="center" gap={1} onClick={(e) => handleFilesClick(e, "file")}
                sx={{
                  cursor: isCombinedKB
                    ? (props?.knowledgeBase?.files?.items && props.knowledgeBase.files.items.length > 0 ? "pointer" : "default")
                    : (props?.knowledgeBase?.files?.count && props.knowledgeBase.files?.count > 0 ? "pointer" : "default")
                }}
              >
                <img src={CustomFileIcon} alt="file" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="source-text"
                  sx={{
                    transition: "color 0.2s",
                  }}
                >
                  {isCombinedKB
                    ? (props?.knowledgeBase?.files?.items ? props.knowledgeBase.files.items.length : 0)
                    : (props?.knowledgeBase?.files?.count && props.knowledgeBase.files.count > 0 ? props.knowledgeBase.files.count : 0)
                  } Files
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={700}
                color="text.secondary"
                className="source-text"
                sx={{ transition: "color 0.2s" }}
              >

                {props.knowledgeBase.files?.total_size_bytes ? Math.round(props.knowledgeBase.files.total_size_bytes / 1024) + " KB" : 0 + " KB"}
              </Typography>
            </Box>
            <Popover
              open={filesPopoverOpen}
              anchorEl={filesPopoverAnchor}
              onClose={handleFilesPopoverClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  minWidth: 250,
                  maxHeight: 210,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {isCombinedKB ? (
                  (props.knowledgeBase.files?.items && props.knowledgeBase.files.items.length > 0) ? (
                    props.knowledgeBase.files.items.map((fileData: any) => (
                      <Box
                        key={fileData?.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        {getFileIcon(fileData?.filename)}
                        <Tooltip title={fileData.filename} placement="top-start">
                          <Typography
                            noWrap
                            sx={{ maxWidth: 220, minWidth: 220 }}
                          >
                            {fileData.filename}
                          </Typography>
                        </Tooltip>
                        {/* No delete for combined KB files */}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">No files found.</Typography>
                  )
                ) : (
                  filesLoading ? (
                    <Box
                      sx={{
                        p: 2,
                        minWidth: 250,
                        maxHeight: 210,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <CircularProgress color="primary" />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading files...
                      </Typography>
                    </Box>
                  ) : (
                    // props.knowledgeBase?.files?.items?.map((fileData: KnowledgeBaseFileData) => (
                    knowledgeBaseFiles?.map((fileData: any) => (
                      <Box
                        key={fileData?.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        {getFileIcon(fileData?.filename)}
                        <Tooltip title={fileData.filename} placement="top-start">
                          <Typography
                            noWrap
                            sx={{ maxWidth: 220, minWidth: 220 }}
                          >
                            {fileData.filename}
                          </Typography>
                        </Tooltip>
                        {fileData.processing_status == KnowledgeBaseFileStatusEnum.COMPLETED && (<>
                          <IconButton
                            disabled={fileData.processing_status !== KnowledgeBaseFileStatusEnum.COMPLETED}
                            title="delete"
                            color="error"
                            aria-label="delete"
                            onClick={() => {
                              confirmDelete(fileData.id);
                              handleFilesPopoverClose();
                            }}
                            size="small"
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </>)
                        }
                        {fileData.processing_status == KnowledgeBaseFileStatusEnum.PROCESSING && (<>
                          <IconButton
                            color="info"
                            title="processing"
                            aria-label="processing"
                            onClick={() => { }}
                            size="small"
                          >
                            <AccessTimeOutlinedIcon fontSize="small" />
                          </IconButton>
                        </>)
                        }
                        {fileData.processing_status == KnowledgeBaseFileStatusEnum.PENDING && (<>
                          <IconButton
                            color="info"
                            title="pending"
                            aria-label="pending"
                            onClick={() => { }}
                            size="small"
                          >
                            <HourglassTopOutlinedIcon fontSize="small" />
                          </IconButton>
                        </>)
                        } {fileData.processing_status == KnowledgeBaseFileStatusEnum.FAILED && (<>
                          <IconButton
                            color="info"
                            title="failed"
                            aria-label="failed"
                            onClick={() => { }}
                            size="small"
                          >
                            <ErrorOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </>)
                        }
                      </Box>
                    ))
                  )
                )}
              </Box>
            </Popover>
            {/* </Box> */}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1} onClick={(e) => handleFilesClick(e, "link")}
                sx={{
                  cursor: isCombinedKB
                    ? (props?.knowledgeBase?.links?.items && props.knowledgeBase.links.items.length > 0 ? "pointer" : "default")
                    : (props?.knowledgeBase?.links && props.knowledgeBase.links.count > 0 ? "pointer" : "default")
                }}
              >
                <img src={CustomLinkIcon} alt="file" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="source-text"
                  sx={{ transition: "color 0.2s" }}
                >
                  {isCombinedKB
                    ? (props?.knowledgeBase?.links?.items ? props.knowledgeBase.links.items.length : 0)
                    : (props?.knowledgeBase?.links && props.knowledgeBase.links.count > 0 ? props.knowledgeBase.links.count : 0)
                  } Links
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={700}
                color="text.secondary"
                className="source-text"
                sx={{ transition: "color 0.2s" }}
              >

                {props.knowledgeBase.links?.total_size_bytes ? Math.round(props.knowledgeBase.links.total_size_bytes / 1024) + " KB" : 0 + " KB"}
              </Typography>
              <Popover
                open={linkPopoverOpen}
                anchorEl={linksPopoverAnchor}
                onClose={handleLinkPopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    minWidth: 250,
                    maxHeight: 210,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {isCombinedKB ? (
                    (props.knowledgeBase.links?.items && props.knowledgeBase.links.items.length > 0) ? (
                      props.knowledgeBase.links.items.map((urlData: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <img src={CustomLinkIcon} alt="link" />
                          <Tooltip title={urlData.url} placement="top-start">
                            <Typography
                              noWrap
                              sx={{ maxWidth: 220, minWidth: 220 }}
                            >
                              {urlData.url}
                            </Typography>
                          </Tooltip>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2">No links found.</Typography>
                    )
                  ) : (
                    props.knowledgeBase?.links?.items && props.knowledgeBase.links.items.length > 0 ? (
                      props.knowledgeBase.links.items.map((urlData: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <img src={CustomLinkIcon} alt="link" />
                          <Tooltip title={urlData.url} placement="top-start">
                            <Typography
                              noWrap
                              sx={{ maxWidth: 220, minWidth: 220 }}
                            >
                              {urlData.url}
                            </Typography>
                          </Tooltip>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2">No links found.</Typography>
                    )
                  )}
                </Box>
              </Popover>
            </Box>
          </Box>

        </Box>

      </CardContent >
      <Divider sx={{ width: "100%" }} />

      {/* Actions */}
      <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {/* <Typography variant="body2" fontWeight={400} color="text.secondary">
            Characters Trained:
          </Typography>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            1074
          </Typography> */}
        </Box>
        <Box display="flex">
          <IconButton component="label" disabled={props.knowledgeBase.type !== KnowledgeBaseTypeEnum.PDF} title="Upload Files">
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.txt,.ppt,.pptx"
              disabled={props.knowledgeBase.type !== KnowledgeBaseTypeEnum.PDF}
            />
            <FileUploadOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={() => props.getKnowledgeBases()} title="Refresh">
            <CachedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={() => props.editFun(props.knowledgeBase)} title="Knowledge settings">
            <SettingsOutlined sx={{ fontSize: "1rem" }} />
          </IconButton>
          <IconButton
            onClick={() => props.deleteFun(props.knowledgeBase)}
            title="Delete"
          >
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardActions>
      <ConfirmPopup details={confirmPopupData} />
    </Card >
  );
};

export default KnowledgeBaseCard;
